import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {HttpUtils} from '../shared/http.utils';
import {Injectable} from '@angular/core';
import {AlarmFilters} from '../model/filters/alarm-filters';
import {AlarmDefinitionFilters} from '../model/filters/alarm-definition-filters';
import {AlarmPersistRequest} from '../model/requests/alarm-persist-request';
import {AlarmDefinitionResponse} from '../model/responses/alarm-definition-response';
import {AlarmDefinition} from '../model/alarm-definition';
import {AlarmPageableResponse} from '../model/responses/alarm-pageable-response';
import {AlarmDefinitionSummaryResponse} from '../model/responses/alarm-definition-summary-response';
import {AlarmsChartsResponse} from '../model/responses/alarms-charts-response';
import {MachineAlarmsChartsResponse} from '../model/responses/machine-alarms-charts-response';
import {AlarmSubscription} from "../model/alarm-subscription";
import {AlarmSubscriptionPersistRequest} from "../model/requests/alarm-subscription-persist-request";
import {FiltersUtils} from "../shared/filters.utils";

@Injectable({
  providedIn: 'root'
})
export class AlarmsService {

  private readonly alarmsURL: string;


  constructor(private http: HttpClient) {
    this.alarmsURL = '/api/alarms';
  }

  public getAlarmsDefinition(offset?: number, limit?: number, filters?: AlarmDefinitionFilters): Observable<AlarmDefinitionResponse> {
    let options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    if (filters != null) {
      options = AlarmsService.setAlarmDefinitionParameters(options, filters);
    }
    const url: string = `${this.alarmsURL}/definition`;

    return this.http.get<AlarmDefinitionResponse>(url, options);
  }

  public getAlarmsDefinitionSummary(organizationUuid: string): Observable<AlarmDefinitionSummaryResponse> {
    const url: string = `${this.alarmsURL}/summary/${organizationUuid}`;
    return this.http.get<AlarmDefinitionSummaryResponse>(url);
  }

  public getAlarmsDefinitionFilters(): Observable<AlarmDefinitionSummaryResponse> {
    const url: string = `${this.alarmsURL}/filters`;
    return this.http.get<AlarmDefinitionSummaryResponse>(url);
  }

  public createAlarmDefinition(alarmPersistData: AlarmPersistRequest): Observable<Response> {
    const url: string = `${this.alarmsURL}`;
    return this.http.post<Response>(url, alarmPersistData);
  }

  public getAlarmDefinitionByUuid(alarmDefinitionUuid: string): Observable<{ alarmDefinition: AlarmDefinition }> {
    const url: string = `${this.alarmsURL}/${alarmDefinitionUuid}`;
    return this.http.get<{ alarmDefinition: AlarmDefinition }>(url);
  }

  public editAlarmDefinition(alarmDefinitionUuid: string, alarmPersistData: AlarmPersistRequest): Observable<Response> {
    const url: string = `${this.alarmsURL}/${alarmDefinitionUuid}`;
    return this.http.put<Response>(url, alarmPersistData);
  }

  public deleteAlarmDefinition(alarmDefinitionUuid: string): Observable<Response> {
    const url: string = `${this.alarmsURL}/${alarmDefinitionUuid}`;
    return this.http.delete<Response>(url);
  }

  private static setAlarmDefinitionParameters(options: any, filters: AlarmDefinitionFilters): any {
    options.params["name"] = FiltersUtils.getFilterTrim(filters.name);
    options.params["sensors"] = FiltersUtils.getFilter(filters.sensors);
    options.params["operations"] = FiltersUtils.getFilter(filters.operations);
    options.params["sortField"] = FiltersUtils.getFilter(filters.sortField);
    options.params["sortOrder"] = FiltersUtils.getFilter(filters.sortOrder);
    return options;
  }

  public getAlarms(offset?: number, limit?: number, filters?: AlarmFilters): Observable<AlarmPageableResponse> {
    let options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    if (filters != null) {
      options = AlarmsService.setAlarmParameters(options, filters);
    }
    const url: string = `${this.alarmsURL}`;

    return this.http.get<AlarmPageableResponse>(url, options);
  }

  public getAlarmsByMachineSerial(serial: string, offset?: number, limit?: number, filters?: AlarmFilters):
    Observable<AlarmPageableResponse> {
    let options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    if (filters != null) {
      options = AlarmsService.setAlarmParameters(options, filters);
    }
    const url: string = `${this.alarmsURL}/machine/${serial}`;
    return this.http.get<AlarmPageableResponse>(url, options);
  }

  public getAlarmsCharts(): Observable<AlarmsChartsResponse> {
    const url: string = `${this.alarmsURL}/charts`;
    return this.http.get<AlarmsChartsResponse>(url);
  }

  public getAlarmsChartsByMachineUuid(machineUuid: string): Observable<MachineAlarmsChartsResponse> {
    const url: string = `${this.alarmsURL}/charts/${machineUuid}`;
    return this.http.get<MachineAlarmsChartsResponse>(url);
  }

  public deactivateAlarm(alarmUuid: string): Observable<void> {
    const url: string = `${this.alarmsURL}/reset/${alarmUuid}`;
    return this.http.put<void>(url, {});
  }

  public getAlarmSubscriptionsByOrganizationUuid(organizationUuid: string): Observable<{
    alarmSubscriptions: AlarmSubscription[]
  }> {
    const url: string = `${this.alarmsURL}/subscriptions/${organizationUuid}`;
    return this.http.get<{ alarmSubscriptions: AlarmSubscription[] }>(url);
  }

  public subscribeOrganizationToAlarms(organizationUuid: string, alarmSubscriptionPersistData: AlarmSubscriptionPersistRequest): Observable<void> {
    const url: string = `${this.alarmsURL}/subscribe/${organizationUuid}`;
    return this.http.post<void>(url, alarmSubscriptionPersistData);
  }

  public getAlarmsExcelByMachineSerial(serial: string, filters: AlarmFilters, fileName: string): Observable<Blob> {
    let options = HttpUtils.buildOptions(HttpUtils.buildListParams());
    if (filters != null) {
      options = AlarmsService.setAlarmParameters(options, filters);
    }
    const url: string = `${this.alarmsURL}/machine/${serial}/excel/${fileName}`;
    return this.http.get(url, {params: options.params, responseType: 'blob'});
  }

  private static setAlarmParameters(options: any, filters: AlarmFilters): any {
    options.params["alarmDefinitionName"] = FiltersUtils.getFilterTrim(filters.alarmDefinitionName);
    options.params["machineSerialNumber"] = FiltersUtils.getFilterTrim(filters.machineSerialNumber);
    options.params["activationDate"] = FiltersUtils.getFilter(filters.activationDate);
    options.params["deactivationDate"] = FiltersUtils.getFilter(filters.deactivationDate);
    options.params["resetUsername"] = FiltersUtils.getFilterTrim(filters.resetUsername);
    options.params["alarmType"] = FiltersUtils.getFilterTrim(filters.alarmType);
    options.params["machineGroupsUuids"] = FiltersUtils.getFilter(filters.machineGroupsUuids);
    options.params["organizationUuids"] = FiltersUtils.getFilter(filters.organizationUuids);
    options.params["active"] = FiltersUtils.getFilterBoolean(filters.active);
    options.params["sortField"] = FiltersUtils.getFilter(filters.sortField);
    options.params["sortOrder"] = FiltersUtils.getFilter(filters.sortOrder);
    return options;
  }

}
